import { motion } from "framer-motion";
import { Globe, Settings2, MonitorSmartphone, ShieldCheck, TrendingUp, Coins, Palette, Users, BarChart3, Infinity as InfinityIcon } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { FeatureList } from "./FeatureList";

const Card = ({ className = "", children, testId, delay = 0 }) => (
  <Reveal delay={delay} className={`h-full ${className}`}>
    <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 260, damping: 22 }} data-testid={testId} className="group relative h-full overflow-hidden rounded-3xl border border-slate-200 bg-white p-7 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] transition-[border-color,box-shadow] duration-500 hover:border-blue-300 hover:shadow-[0_30px_80px_-30px_rgba(37,99,235,0.45)] sm:p-9">
      {children}
    </motion.div>
  </Reveal>
);

const Badge = ({ children }) => <span className="font-mono-x inline-block rounded-full border border-white/50 bg-white/15 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-white">{children}</span>;

const IconBox = ({ Icon, color }) => (
  <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${color} shadow-lg transition-transform duration-500 group-hover:rotate-[-8deg] group-hover:scale-110`}><Icon className="h-7 w-7 text-white" /></div>
);

const STRIP = [[Coins, "Manage Credits", "Set your own rates and packages"], [Palette, "Custom Design", "Make it your own with your branding"], [Users, "Grow Your Membership", "Attract and retain active members"], [BarChart3, "Track Results", "Detailed stats and reports"], [InfinityIcon, "Unlimited Potential", "Your business, your rules"]];

const ADMIN = ["Members", "Sites", "Credits", "Banners", "Payouts"];

export const Bento = () => (
  <section id="features" className="relative mx-auto max-w-7xl px-5 py-28 lg:px-8 lg:py-36" data-testid="features-section">
    <Reveal className="max-w-3xl">
      <Eyebrow>Complete traffic exchange software</Eyebrow>
      <h2 className="font-display mt-6 text-3xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">Everything you need to run a <span className="text-gradient">profitable</span> traffic exchange.</h2>
    </Reveal>
    <FeatureList />
    <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-12 lg:gap-6">
      <Card className="md:col-span-8" testId="bento-hosting">
        <img src="/img/streams.jpg" alt="Streams of web traffic" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.6s] group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-700 via-blue-700/80 to-blue-600/10" />
        <div className="relative flex h-full min-h-[300px] flex-col justify-between gap-10">
          <div className="flex items-center gap-4"><IconBox Icon={Globe} color="bg-emerald-500" /><Badge>Zero overhead</Badge></div>
          <div>
            <h3 className="font-display text-2xl font-extrabold leading-tight text-white sm:text-4xl">Free installation.<br />Free domain.<br /><span className="text-[#FACC15]">Lifetime hosting.</span></h3>
            <p className="mt-4 max-w-md text-blue-50">We set everything up for you, so you can focus on growing members and earning.</p>
          </div>
        </div>
      </Card>
      <Card className="md:col-span-4" testId="bento-admin" delay={0.08}>
        <IconBox Icon={Settings2} color="bg-blue-600" />
        <h3 className="font-display mt-6 text-xl font-bold text-slate-900 sm:text-2xl">Full Admin Control</h3>
        <p className="mt-2 text-slate-600">Manage members, sites, credits and more.</p>
        <div className="mt-6 space-y-2">
          {ADMIN.map((a, i) => (
            <div key={a} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
              {a}<span className={`h-5 w-9 rounded-full p-0.5 ${i === 3 ? "bg-slate-300" : "bg-blue-600"}`}><span className={`block h-4 w-4 rounded-full bg-white transition-transform duration-500 ${i === 3 ? "group-hover:translate-x-4" : "translate-x-4"}`} /></span>
            </div>
          ))}
        </div>
      </Card>
      <Card className="md:col-span-4" testId="bento-responsive">
        <IconBox Icon={MonitorSmartphone} color="bg-violet-600" />
        <h3 className="font-display mt-6 text-xl font-bold text-slate-900 sm:text-2xl">Modern &amp; Responsive</h3>
        <p className="mt-2 text-slate-600">Works on all devices and platforms — phone, tablet and desktop.</p>
        <div className="mt-8 flex items-end gap-3">
          <div className="h-24 flex-1 rounded-lg border-2 border-slate-300 bg-gradient-to-br from-blue-600/40 to-cyan-400/20" />
          <div className="h-20 w-14 rounded-lg border-2 border-slate-300 bg-gradient-to-br from-cyan-400/40 to-blue-600/20" />
          <div className="h-14 w-8 rounded-md border-2 border-slate-300 bg-gradient-to-br from-yellow-300/50 to-blue-600/20" />
        </div>
      </Card>
      <Card className="md:col-span-4" testId="bento-secure" delay={0.08}>
        <IconBox Icon={ShieldCheck} color="bg-amber-500" />
        <h3 className="font-display mt-6 text-xl font-bold text-slate-900 sm:text-2xl">Secure &amp; Reliable</h3>
        <p className="mt-2 text-slate-600">Built with the latest security features to protect you and your members.</p>
        <div className="relative mt-8 flex h-24 items-center justify-center">
          <span className="absolute h-24 w-24 animate-ping rounded-full bg-amber-400/20 [animation-duration:2.5s]" />
          <ShieldCheck className="relative h-14 w-14 text-amber-300" />
        </div>
      </Card>
      <Card className="md:col-span-4" testId="bento-earning" delay={0.16}>
        <img src="/img/growth.jpg" alt="Rising revenue chart" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.6s] group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-blue-800 via-blue-800/60 to-transparent" />
        <div className="relative flex h-full min-h-[280px] flex-col justify-between">
          <IconBox Icon={TrendingUp} color="bg-red-500" />
          <div><h3 className="font-display text-xl font-bold text-white sm:text-2xl">Great Earning Potential</h3><p className="mt-2 text-slate-200">Keep more of what you earn with your own system.</p></div>
        </div>
      </Card>
      <div className="grid grid-cols-1 gap-px overflow-hidden rounded-3xl border border-slate-200 bg-slate-200 sm:grid-cols-2 md:col-span-12 lg:grid-cols-5" data-testid="bento-strip">
        {STRIP.map(([I, t, d], i) => (
          <Reveal key={t} delay={i * 0.06} className="h-full">
            <div className="group h-full bg-white p-7 transition-colors duration-500 hover:bg-blue-50">
              <I className="h-8 w-8 text-blue-600 transition-transform duration-500 group-hover:-translate-y-1" />
              <h4 className="font-display mt-5 text-base font-bold text-slate-900">{t}</h4>
              <p className="mt-1.5 text-sm text-slate-600">{d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
